import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAtom, useAtomValue } from 'jotai'
import { useNavigate } from 'react-router-dom'
import { useDictationContext } from '../../../core/provider/dictation-context.provider'
import { PILL_HEIGHT, PILL_WIDTH, useDraggablePosition } from '../../../hooks/utils/dictation'
import {
  statsAssistantHasProcessingTurnAtom,
  statsAssistantPanelOpenAtom,
} from '../../../state/statsAssistant'
import {
  DictationButtonState,
  getAvailableModes,
  resolveWidgetMode,
  ModeId,
  PANEL_HEIGHT,
  STATS_PANEL_HEIGHT,
  BAR_HEIGHT,
  STORAGE_KEY_MIC,
  STORAGE_KEY_MODE,
} from './DictationWidget.types'

interface UseWidgetStateOptions {
  statsModeEnabled?: boolean
  voiceDictationEnabled?: boolean
  isRendered?: boolean
}

export const useWidgetState = ({
  statsModeEnabled = false,
  voiceDictationEnabled = false,
  isRendered = true,
}: UseWidgetStateOptions = {}) => {
  const navigate = useNavigate()

  const {
    state,
    isRecording,
    currentTarget,
    audioQuality,
    startDictation,
    stopDictation,
    setMicrophoneId,
    notepadTranscript,
    setNotepadTranscript,
    clearNotepadTranscript,
    setNotepadMode,
  } = useDictationContext()

  // ── Device enumeration ──────────────────────────────────────────
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([])
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>()

  const loadDevices = useCallback(async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true })
      const all = await navigator.mediaDevices.enumerateDevices()
      const mics = all.filter((d) => d.kind === 'audioinput' && d.deviceId !== 'default')
      setDevices(mics)
      const savedId = localStorage.getItem(STORAGE_KEY_MIC)
      const savedMic = savedId ? mics.find((m) => m.deviceId === savedId) : null
      if (savedMic) {
        setSelectedDeviceId(savedMic.deviceId)
        setMicrophoneId(savedMic.deviceId)
      } else {
        setSelectedDeviceId(undefined)
      }
    } catch {
      setDevices([])
    }
  }, [setMicrophoneId])

  useEffect(() => {
    loadDevices()
    navigator.mediaDevices.addEventListener('devicechange', loadDevices)
    return () => navigator.mediaDevices.removeEventListener('devicechange', loadDevices)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Widget state ────────────────────────────────────────────────
  const availableModes = getAvailableModes(statsModeEnabled, voiceDictationEnabled)

  const [mode, setMode] = useState<ModeId>(() =>
    resolveWidgetMode(localStorage.getItem(STORAGE_KEY_MODE), availableModes),
  )

  useEffect(() => {
    const modes = getAvailableModes(statsModeEnabled, voiceDictationEnabled)
    if (modes.length === 0) return
    const next = resolveWidgetMode(localStorage.getItem(STORAGE_KEY_MODE), modes)
    setMode((current) => (current === next ? current : next))
  }, [statsModeEnabled, voiceDictationEnabled])

  const [modeOpen, setModeOpen] = useState(false)
  const [micOpen, setMicOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const [statsPanelOpen, setStatsPanelOpen] = useAtom(statsAssistantPanelOpenAtom)
  const hasBackgroundStatsTurn = useAtomValue(statsAssistantHasProcessingTurnAtom)

  // Analyse terminée panneau rabattu : pastille verte jusqu'à la réouverture.
  const [hasUnseenStatsResult, setHasUnseenStatsResult] = useState(false)
  const wasProcessingRef = useRef(hasBackgroundStatsTurn)

  useEffect(() => {
    if (wasProcessingRef.current && !hasBackgroundStatsTurn && !statsPanelOpen) {
      setHasUnseenStatsResult(true)
    }
    wasProcessingRef.current = hasBackgroundStatsTurn
  }, [hasBackgroundStatsTurn, statsPanelOpen])

  useEffect(() => {
    if (statsPanelOpen) setHasUnseenStatsResult(false)
  }, [statsPanelOpen])

  useEffect(() => {
    if (mode !== 'stats') {
      setStatsPanelOpen(false)
    }
  }, [mode, setStatsPanelOpen])

  // ── Drag ────────────────────────────────────────────────────────
  const { position, onMouseDown, wasDragged, resetDrag, widgetRef } = useDraggablePosition(
    PILL_WIDTH,
    PILL_HEIGHT,
    isRendered,
  )

  /** Vrai si le geste qui se termine était un déplacement ; remet l'état à zéro dans tous les cas. */
  const consumeDrag = useCallback(() => {
    const dragged = wasDragged()
    resetDrag()
    return dragged
  }, [wasDragged, resetDrag])

  // ── Panel direction ─────────────────────────────────────────────
  const activePanelHeight = mode === 'stats' ? STATS_PANEL_HEIGHT : PANEL_HEIGHT

  const panelDirection = useMemo<'down' | 'up'>(() => {
    return position.y + BAR_HEIGHT + activePanelHeight > window.innerHeight - 20 ? 'up' : 'down'
  }, [activePanelHeight, position.y])

  // ── Notepad mode signal — tells the provider to route transcripts to notepadTranscript ──
  useEffect(() => {
    setNotepadMode(mode === 'notepad')
    return () => setNotepadMode(false)
  }, [mode, setNotepadMode])

  // ── Handlers ────────────────────────────────────────────────────
  const handleModeChange = useCallback(
    (id: ModeId) => {
      if (wasDragged()) {
        resetDrag()
        return
      }
      resetDrag()
      setMode(id)
      setModeOpen(false)
      if (id === 'stats') {
        setStatsPanelOpen(true)
        if (isRecording) stopDictation()
      } else {
        setStatsPanelOpen(false)
      }
      localStorage.setItem(STORAGE_KEY_MODE, id)
    },
    [isRecording, stopDictation, wasDragged, resetDrag, setStatsPanelOpen],
  )

  const handleMicSelect = useCallback(
    (deviceId: string) => {
      setSelectedDeviceId(deviceId)
      setMicrophoneId(deviceId)
      localStorage.setItem(STORAGE_KEY_MIC, deviceId)
      setMicOpen(false)
    },
    [setMicrophoneId],
  )

  const handleRecordToggle = useCallback(() => {
    if (wasDragged()) {
      resetDrag()
      return
    }
    resetDrag()
    if (!selectedDeviceId || devices.length === 0) return
    if (isRecording) stopDictation()
    else startDictation()
  }, [
    wasDragged,
    resetDrag,
    selectedDeviceId,
    devices.length,
    isRecording,
    stopDictation,
    startDictation,
  ])

  const handleStatsPanelToggle = useCallback(() => {
    if (wasDragged()) {
      resetDrag()
      return
    }
    resetDrag()
    setStatsPanelOpen((open) => !open)
  }, [wasDragged, resetDrag, setStatsPanelOpen])

  const handleStatsPanelOpen = useCallback(() => setStatsPanelOpen(true), [setStatsPanelOpen])

  const handleCopy = useCallback(async () => {
    if (!notepadTranscript) return
    try {
      await navigator.clipboard.writeText(notepadTranscript)
    } catch {}
    setCopied(true)
    setTimeout(() => setCopied(false), 1400)
  }, [notepadTranscript])

  const handleReset = useCallback(() => clearNotepadTranscript(), [clearNotepadTranscript])

  const handleSettingsClick = useCallback(() => {
    if (wasDragged()) {
      resetDrag()
      return
    }
    resetDrag()
    navigate('/account/preferences')
  }, [wasDragged, resetDrag, navigate])

  const handleDropdownToggle = useCallback(
    (which: 'mode' | 'mic') => {
      if (wasDragged()) {
        resetDrag()
        return
      }
      resetDrag()
      if (which === 'mode') {
        setModeOpen((v) => !v)
        setMicOpen(false)
      } else {
        setMicOpen((v) => !v)
        setModeOpen(false)
      }
    },
    [wasDragged, resetDrag],
  )

  // ── Computed ────────────────────────────────────────────────────
  const noMic = devices.length === 0
  const isDisabled = noMic || !selectedDeviceId || state === 'connecting'
  const activeMic = devices.find((d) => d.deviceId === selectedDeviceId)
  const activeMode = availableModes.find((m) => m.id === mode) ?? availableModes[0]

  const buttonState = useMemo<DictationButtonState>(() => {
    if (noMic || !selectedDeviceId) return 'disabled'
    if (state === 'error') return 'error'
    if (state === 'connecting') return 'connecting'
    if (isRecording) {
      if (audioQuality === 'too_noisy') return 'recordingTooNoisy'
      if (audioQuality === 'warning') return 'recordingWarning'
      if (mode === 'notepad') return 'recordingTargeted'
      return currentTarget !== null ? 'recordingTargeted' : 'recordingUntargeted'
    }
    return 'idle'
  }, [mode, noMic, selectedDeviceId, state, isRecording, audioQuality, currentTarget])

  return {
    position,
    widgetRef,
    onMouseDown,
    consumeDrag,
    panelDirection,
    mode,
    devices,
    selectedDeviceId,
    modeOpen,
    setModeOpen,
    micOpen,
    setMicOpen,
    transcript: notepadTranscript,
    setTranscript: setNotepadTranscript,
    copied,
    isDisabled,
    isRecording,
    activeMic,
    activeMode,
    availableModes,
    buttonState,
    statsPanelOpen,
    hasBackgroundStatsTurn,
    hasUnseenStatsResult,
    handleModeChange,
    handleMicSelect,
    handleRecordToggle,
    handleStatsPanelToggle,
    handleStatsPanelOpen,
    handleCopy,
    handleReset,
    handleSettingsClick,
    handleDropdownToggle,
  }
}
